import axios from "axios";
import { ApiConstructabilityIssue, ApiReference } from "../shared/interfaces";
import { IssueStatus } from "../components/workflow/IssueStatusChip";

export const WorkflowApi = {
    getConstructabilityIssues: async (): Promise<ApiConstructabilityIssue[]> => {
        const resp = await axios.get("/api/workflow");
        return resp.data;
    },

    generateDraftRfi: async (
        node1Id: string,
        node2Id: string,
        description: string,
    ): Promise<string> => {
        const resp = await axios.post("/api/workflow/draftRfi", {
            node1Id,
            node2Id,
            description,
        });
        return resp.data.text;
    },

    getFragmentNodeEmbeddingText: async (fragmentUuid: string): Promise<string> => {
        const resp = await axios.get(`/api/fragment_node/${fragmentUuid}/embedding_text`);
        return resp.data.text;
    },

    startFindProblems: async (): Promise<ApiConstructabilityIssue[]> => {
        const resp = await axios.post("/api/ai/workflow/startFindProblems");
        return resp.data;
    },

    updateConstructabilityIssueSeverity: async (
        id: string,
        severity: string,
    ): Promise<ApiConstructabilityIssue> => {
        const resp = await axios.put<ApiConstructabilityIssue>(
            `/api/ai/constructability_issue/${id}/severity`,
            { value: severity },
        );
        return resp.data;
    },

    updateConstructabilityIssueStatus: async (
        id: string,
        status: IssueStatus,
    ): Promise<ApiConstructabilityIssue> => {
        const resp = await axios.put<ApiConstructabilityIssue>(
            `/api/ai/constructability_issue/${id}/status`,
            { value: status },
        );
        return resp.data;
    },

    updateConstructabilityIssueBallInCourt: async (
        id: string,
        ballInCourt: string,
    ): Promise<ApiConstructabilityIssue> => {
        const resp = await axios.put<ApiConstructabilityIssue>(
            `/api/ai/constructability_issue/${id}/ball_in_court`,
            { value: ballInCourt },
        );
        return resp.data;
    },

    extractRfiQuestion: async (id: string): Promise<{ question: string }> => {
        // Get the file's blob name or other metadata first if needed
        const resp = await axios.post("/api/workflow/extractRfiQuestion", {
            fileId: id, // Change from blobName to fileId to indicate we're passing a file ID
        });
        return resp.data;
    },

    checkRfiNecessity: async (
        id: string,
    ): Promise<{
        needsRfi: boolean;
        reviewResponse: string;
        relevantSubsections: string[];
        references?: Record<string, ApiReference>;
    }> => {
        const resp = await axios.post("/api/workflow/checkRfiNecessity", {
            fileId: id,
        });
        return resp.data;
    },

    checkRfiNecessityText: async (
        rfiText: string,
    ): Promise<{
        needsRfi: boolean;
        reviewResponse: string;
        relevantSubsections: string[];
        references?: Record<string, ApiReference>;
    }> => {
        const resp = await axios.post("/api/workflow/checkRfiNecessityText", {
            rfiText,
        });
        return resp.data;
    },

    createRfi: async (
        id: string,
        reviewResponse: string,
        relevantSubsections: string[],
    ): Promise<{
        rfiDraft: string;
        references?: Record<string, ApiReference>;
    }> => {
        const resp = await axios.post("/api/workflow/createRfi", {
            fileId: id,
            reviewResponse,
            relevantSubsections,
        });
        return resp.data;
    },

    createRfiText: async (
        rfiText: string,
        reviewResponse: string,
        relevantSubsections: string[],
    ): Promise<{
        rfiDraft: string;
        references?: Record<string, ApiReference>;
    }> => {
        const resp = await axios.post("/api/workflow/createRfiText", {
            rfiText,
            reviewResponse,
            relevantSubsections,
        });
        return resp.data;
    },

    reviewRfi: async (id: string): Promise<string> => {
        const resp = await axios.post("/api/workflow/reviewRfi", {
            id,
        });
        return resp.data.response;
    },
};
